import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { validateSwedishOrganizationNumber } from '../../../../../../utils/validation';
import { ByteConvertUtils } from '../../../../../../utils/conversion';
import { RootState } from '../../../../../../global';
import { FieldLabels, FieldProps } from '../../../../../../utils/types';
import {
  SubmissionInformationFormState,
  SubmissionInformationStoreState,
} from './submission-information-types';

type ValidationKeys = {
  [key in keyof typeof SubmissionInformationField]: unknown;
};

const SUPPORTED_FILE_FORMATS = ['application/pdf'];

const validateFile = () =>
  yup
    .mixed()
    .test('fileSize', 'En fil får inte vara större än 10 MB', (fileList) => {
      if (fileList.length === 0) return true;

      return fileList[0]?.size < ByteConvertUtils.mbToBytes(10);
    })
    .test('fileType', 'Ogiltig filtyp', (fileList) => {
      if (fileList.length === 0) return true;

      return SUPPORTED_FILE_FORMATS.includes(fileList[0]?.type);
    });

export const submissionInformationValidationSchema = yup
  .object()
  .shape<ValidationKeys>({
    invoiceNumber: yup.string().required('Fakturanummer krävs'),
    internalManagerId: yup.string().nullable().required('Handläggare krävs'),
    organizationName: yup.string().required('Organisationens namn krävs'),
    organizationNumber: validateSwedishOrganizationNumber
      .nullable()
      .required('Organisationsnummer krävs'),
    registrationEvidence: validateFile(),
    memorandumOfAssociation: validateFile(),
    registerData: validateFile(),
    internalDealDate: yup
      .string()
      .nullable()
      .required('Internt transaktionsdatum krävs'),
    registrationDate: yup
      .string()
      .nullable()
      .required('Registreringsdatum krävs'),
    internalManager: yup.object().shape({
      id: yup.string(),
      value: yup.string(),
    }),
  });

export const SubmissionInformationFieldName: FieldProps<
  Omit<SubmissionInformationStoreState, 'files'>
> = {
  organizationName: 'organizationName',
  internalDealDate: 'internalDealDate',
  invoiceNumber: 'invoiceNumber',
  registrationDate: 'registrationDate',
  internalManagerId: 'internalManagerId',
  organizationNumber: 'organizationNumber',
  internalManager: 'internalManager',
};

export const SubmissionInformationField: FieldLabels<SubmissionInformationFormState> = {
  internalDealDate: 'Affärsdatum',
  invoiceNumber: 'Fakturanummer',
  internalManagerId: 'Handläggare: Namn',
  organizationName: 'Tilldelat lagerbolag: Namn',
  registrationDate: 'Tilldelat lagerbolag: Registreringsdatum',
  organizationNumber: 'Tilldelat lagerbolag: OrgNr',
  registrationEvidence: 'Tilldelat lagerbolag: Reg. Bevis',
  memorandumOfAssociation: 'Tilldelat lagerbolag: Stiftelseurkund',
  registerData: 'Färdigregistrerad ändring: Registeruppgift',
  internalManager: 'Handläggare: Namn',
};

export const SubmissionInformationFileName = {
  registrationEvidence: 'RegistrationCertificate.pdf',
  memorandumOfAssociation: 'MemorandumAssociation.pdf',
  registerData: 'RegisterData.pdf',
};

export const useSelectSubmissionInformation = (id: string) =>
  useSelector((state: RootState) => state.buyOrderEdit[id].metadata);
